// The base domain that tenant subdomains hang off, e.g. "3b.run" for a wildcard
// of "cap.3b.run". tenants.public_domain stores only the tenant label ("cap");
// the base is universal across a stack's tenants and derived from the wildcard
// env value by dropping its first label. Empty when isolation is off (no
// wildcard) — callers then fall back to the bare public host.
export function publicBaseDomain(wildcardDomain: string | undefined): string {
  if (!wildcardDomain) return "";
  const dot = wildcardDomain.indexOf(".");
  return dot === -1 ? "" : wildcardDomain.slice(dot + 1);
}

type PublicWildcardConfig = {
  publicWildcardDomain?: string;
};

// Routes may repeat across spaces only when each space has its own public host.
export function publicRouteScope(
  config: PublicWildcardConfig
): "space" | "tenant" {
  return config.publicWildcardDomain ? "space" : "tenant";
}

export function spacePublicHost(
  spaceSlug: string,
  wildcardDomain: string,
  combinedSpaceTenantLabel?: string
): string {
  const label = combinedSpaceTenantLabel
    ? `${spaceSlug}--${combinedSpaceTenantLabel}`
    : spaceSlug;
  return `${label}.${wildcardDomain}`;
}

function spacePublicUrl(
  spaceSlug: string,
  route: string,
  wildcardDomain: string,
  combinedSpaceTenantLabel?: string
): string {
  return `https://${spacePublicHost(
    spaceSlug,
    wildcardDomain,
    combinedSpaceTenantLabel
  )}${route}`;
}

export function publicRouteUrl(
  spaceSlug: string,
  route: string,
  opts: {
    publicUrl: string;
    publicWildcardDomain?: string;
    combinedSpaceTenantLabel?: string;
    customDomain?: string | null;
  },
  params?: { branchId?: string | null; externalId?: string | null }
): string {
  // Custom domain: slug is the leftmost label — mirrors resolveRouteScopeFromHost.
  const base = opts.customDomain
    ? `https://${spaceSlug}.${opts.customDomain}${route}`
    : opts.publicWildcardDomain
      ? spacePublicUrl(
          spaceSlug,
          route,
          opts.publicWildcardDomain,
          opts.combinedSpaceTenantLabel
        )
      : `${opts.publicUrl}${route}`;
  const search = new URLSearchParams();
  if (params?.branchId) search.set("branch", params.branchId);
  if (params?.externalId) search.set("external_id", params.externalId);
  const qs = search.toString();
  return qs ? `${base}?${qs}` : base;
}
